import { toMediaSrc } from './imageProxy';
import { UPLOAD_LIMITS, UPLOAD_LIMIT_LABELS } from './uploadLimits';

/**
 * Mirror of the backend `machine_video/video-source.utils.ts`: recognises the
 * accepted platforms so the dialog can validate a pasted link before sending it.
 */

const FILE_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.ogv', '.mov', '.m4v'];

export const VIDEO_PROVIDER_LABELS = {
  youtube: 'YouTube',
  vimeo: 'Vimeo',
  dailymotion: 'Dailymotion',
  file: 'Lien direct',
};

const stripWww = (host) => host.replace(/^www\./i, '');

const parseYoutube = (url) => {
  const host = stripWww(url.hostname).toLowerCase();
  let id = null;

  if (host === 'youtu.be') {
    id = url.pathname.slice(1).split('/')[0] || null;
  } else if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    if (url.pathname === '/watch') {
      id = url.searchParams.get('v');
    } else if (url.pathname.startsWith('/embed/') || url.pathname.startsWith('/shorts/')) {
      id = url.pathname.split('/')[2] || null;
    }
  }

  if (!id || !/^[\w-]{6,}$/.test(id)) return null;

  return {
    provider: 'youtube',
    externalUrl: `https://www.youtube.com/watch?v=${id}`,
    embedUrl: `https://www.youtube-nocookie.com/embed/${id}`,
  };
};

const parseVimeo = (url) => {
  const host = stripWww(url.hostname).toLowerCase();
  if (host !== 'vimeo.com' && host !== 'player.vimeo.com') return null;

  const id = url.pathname
    .split('/')
    .filter(Boolean)
    .find((segment) => /^\d{6,}$/.test(segment));
  if (!id) return null;

  return {
    provider: 'vimeo',
    externalUrl: `https://vimeo.com/${id}`,
    embedUrl: `https://player.vimeo.com/video/${id}`,
  };
};

const parseDailymotion = (url) => {
  const host = stripWww(url.hostname).toLowerCase();
  let id = null;

  if (host === 'dai.ly') {
    id = url.pathname.slice(1).split('/')[0] || null;
  } else if (host === 'dailymotion.com') {
    const segments = url.pathname.split('/').filter(Boolean);
    const index = segments.indexOf('video');
    id = index >= 0 ? segments[index + 1] : null;
  }

  const cleanId = id ? id.split('_')[0] : null;
  if (!cleanId || !/^[a-z0-9]{5,}$/i.test(cleanId)) return null;

  return {
    provider: 'dailymotion',
    externalUrl: `https://www.dailymotion.com/video/${cleanId}`,
    embedUrl: `https://www.dailymotion.com/embed/video/${cleanId}`,
  };
};

const parseDirectFile = (url) => {
  const path = url.pathname.toLowerCase();
  if (!FILE_EXTENSIONS.some((extension) => path.endsWith(extension))) return null;
  return { provider: 'file', externalUrl: url.toString(), embedUrl: null };
};

export const parseVideoLink = (rawUrl) => {
  if (typeof rawUrl !== 'string' || rawUrl.trim() === '') return null;

  let url;
  try {
    url = new URL(rawUrl.trim());
  } catch {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  return parseYoutube(url) || parseVimeo(url) || parseDailymotion(url) || parseDirectFile(url) || null;
};

export const ALLOWED_VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/webm',
];

export const MAX_VIDEO_UPLOAD_BYTES = UPLOAD_LIMITS.videoBytes;

export const validateVideoFile = (file) => {
  if (!file) return 'Aucun fichier sélectionné.';
  if (!ALLOWED_VIDEO_MIME_TYPES.includes(String(file.type).toLowerCase())) {
    return 'Formats acceptés : MP4, MOV, AVI, WebM.';
  }
  if (file.size > MAX_VIDEO_UPLOAD_BYTES) {
    return `La vidéo ne doit pas dépasser ${UPLOAD_LIMIT_LABELS.video}.`;
  }
  return null;
};

/**
 * Poster image for a card. Only YouTube exposes one without an API call;
 * everything else falls back to the placeholder tile of the gallery.
 */
export const videoThumbnailUrl = (video) => {
  if (video?.provider !== 'youtube' || !video.embed_url) return null;
  const id = String(video.embed_url).split('/').pop();
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};

/**
 * Normalises a video record (machine video or article media) into what the
 * player needs: an iframe embed for platforms, a streamable source otherwise.
 */
export const resolveVideoPlayback = (video) => {
  if (!video) return null;

  if (video.source_kind === 'link' || (!video.display_url && video.external_url)) {
    return {
      kind: video.embed_url ? 'embed' : 'file',
      src: video.embed_url || video.external_url,
      externalUrl: video.external_url || null,
      provider: video.provider || 'file',
    };
  }

  const src = toMediaSrc(video.display_url);
  return src ? { kind: 'file', src, externalUrl: null, provider: 'file' } : null;
};
